import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Copy, HardDrive, KeyRound, Plus, ShieldAlert, Undo2 } from 'lucide-react';
import { Badge, type BadgeTone } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { useServiceDeskAccess } from '../api';
import { formatDateTime, humanize } from '../viewModel';
import {
  useAllocationDevices,
  useAssetAllocations,
  useAssetAllocationMutations,
  type AllocationDevice,
} from '../assetAllocation';

function errorMessage(err: unknown, fallback: string): string {
  const maybe = err as { response?: { data?: { error?: string } } };
  return maybe?.response?.data?.error || fallback;
}

function statusTone(status: string): BadgeTone {
  return status === 'allocated' ? 'info' : 'neutral';
}

const today = () => new Date().toISOString().slice(0, 10);

interface FormState {
  assetId: string;
  category: string;
  brand: string;
  modelNumber: string;
  serialNumber: string;
  warranty: string;
  employeeUserId: string;
  employeeDepartment: string;
  dateOfAssigning: string;
  devicePassword: string;
}

const EMPTY_FORM: FormState = {
  assetId: '',
  category: '',
  brand: '',
  modelNumber: '',
  serialNumber: '',
  warranty: '',
  employeeUserId: '',
  employeeDepartment: '',
  dateOfAssigning: today(),
  devicePassword: '',
};

export function AssetAllocationSection() {
  const { data: access } = useServiceDeskAccess();
  const isManager = access?.canAllocateAssets ?? false;
  const canReveal = access?.canRevealPasswords ?? false;

  const { data: allocations = [], isLoading } = useAssetAllocations(isManager);
  const { data: devices = [] } = useAllocationDevices(isManager, true);
  const { data: users = [] } = useUserOptions(null);
  const mutations = useAssetAllocationMutations();

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [revealed, setRevealed] = useState<{ assetCode: string; password: string } | null>(null);

  const deviceById = useMemo(() => {
    const map = new Map<string, AllocationDevice>();
    for (const d of devices) map.set(d._id, d);
    return map;
  }, [devices]);

  if (!isManager) {
    return (
      <Card>
        <CardHeader title="Asset Allocation" subtitle="Allocate company devices to employees" />
        <CardBody>
          <EmptyState
            icon={<ShieldAlert className="h-8 w-8" aria-hidden />}
            title="Restricted"
            description="Asset allocation is available to HR administrators only."
          />
        </CardBody>
      </Card>
    );
  }

  const selectDevice = (assetId: string) => {
    const device = deviceById.get(assetId);
    setForm((prev) => ({
      ...prev,
      assetId,
      category: device?.category ?? '',
      brand: device?.brand ?? '',
      modelNumber: device?.modelNumber ?? '',
      serialNumber: device?.serialNumber ?? '',
      warranty: device?.warranty ?? '',
    }));
  };

  const selectEmployee = (employeeUserId: string) => {
    const employee = users.find((u) => u.id === employeeUserId);
    setForm((prev) => ({
      ...prev,
      employeeUserId,
      employeeDepartment: employee?.department ?? '',
    }));
  };

  const resetForm = () => setForm({ ...EMPTY_FORM, dateOfAssigning: today() });

  const submit = () => {
    if (!form.assetId) return toast.error('Select a device to allocate.');
    if (!form.employeeUserId) return toast.error('Select an employee.');
    if (!form.dateOfAssigning) return toast.error('Date of assigning is required.');
    mutations.createAllocation.mutate(
      {
        assetId: form.assetId,
        employeeUserId: form.employeeUserId,
        dateOfAssigning: form.dateOfAssigning,
        brand: form.brand.trim(),
        modelNumber: form.modelNumber.trim(),
        serialNumber: form.serialNumber.trim(),
        warranty: form.warranty.trim(),
        devicePassword: form.devicePassword,
      },
      {
        onSuccess: () => {
          toast.success('Device allocated.');
          resetForm();
          setModalOpen(false);
        },
        onError: (err) => toast.error(errorMessage(err, 'Failed to allocate device.')),
      },
    );
  };

  const reveal = (id: string, assetCode: string) => {
    mutations.revealPassword.mutate(id, {
      onSuccess: (data) => setRevealed({ assetCode, password: data.password || '(no password stored)' }),
      onError: (err) => toast.error(errorMessage(err, 'Failed to reveal password.')),
    });
  };

  const returnDevice = (id: string) => {
    if (!window.confirm('Return this device? It will become available for reallocation.')) return;
    mutations.returnAllocation.mutate(id, {
      onSuccess: () => toast.success('Device returned.'),
      onError: (err) => toast.error(errorMessage(err, 'Failed to return device.')),
    });
  };

  const copyPassword = async () => {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed.password);
      toast.success('Password copied.');
    } catch {
      toast.error('Could not copy to clipboard.');
    }
  };

  return (
    <>
      <Card>
        <CardHeader
          title="Asset Allocation"
          subtitle={`${allocations.length} allocation${allocations.length === 1 ? '' : 's'} · devices sourced from Fixed Assets`}
          action={
            <Button onClick={() => { resetForm(); setModalOpen(true); }}>
              <Plus className="h-4 w-4" aria-hidden />
              Allocate Device
            </Button>
          }
        />
        <CardBody className="p-0">
          {isLoading ? (
            <div className="flex h-24 items-center justify-center">
              <Spinner />
            </div>
          ) : allocations.length === 0 ? (
            <div className="p-5">
              <EmptyState
                icon={<HardDrive className="h-8 w-8" aria-hidden />}
                title="No devices allocated"
                description="Allocate a device from the Fixed Asset register to an employee to get started."
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[880px] text-left text-body-sm">
                <caption className="sr-only">Asset allocations</caption>
                <thead className="text-on-surface-variant">
                  <tr>
                    <th scope="col" className="px-3 py-2 font-medium">Asset ID</th>
                    <th scope="col" className="px-3 py-2 font-medium">Device</th>
                    <th scope="col" className="px-3 py-2 font-medium">Brand / Model</th>
                    <th scope="col" className="px-3 py-2 font-medium">S/N</th>
                    <th scope="col" className="px-3 py-2 font-medium">Employee</th>
                    <th scope="col" className="px-3 py-2 font-medium">Department</th>
                    <th scope="col" className="px-3 py-2 font-medium">Assigned On</th>
                    <th scope="col" className="px-3 py-2 font-medium">Status</th>
                    <th scope="col" className="px-3 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allocations.map((a) => (
                    <tr key={a._id} className="border-t border-outline">
                      <td className="px-3 py-2 font-mono text-data-md text-on-surface">{a.assetCode || '—'}</td>
                      <td className="px-3 py-2 text-on-surface">
                        {a.assetName}
                        {a.category ? <span className="text-on-surface-variant"> · {humanize(a.category)}</span> : null}
                      </td>
                      <td className="px-3 py-2 text-on-surface-variant">
                        {[a.brand, a.modelNumber].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className="px-3 py-2 font-mono text-on-surface-variant">{a.serialNumber || '—'}</td>
                      <td className="px-3 py-2 text-on-surface">{a.employeeName}</td>
                      <td className="px-3 py-2 text-on-surface-variant">{a.employeeDepartment ? humanize(a.employeeDepartment) : '—'}</td>
                      <td className="px-3 py-2 text-on-surface-variant">{formatDateTime(a.dateOfAssigning)}</td>
                      <td className="px-3 py-2"><Badge tone={statusTone(a.status)}>{humanize(a.status)}</Badge></td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          {canReveal && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => reveal(a._id, a.assetCode)}
                              loading={mutations.revealPassword.isPending}
                            >
                              <KeyRound className="h-4 w-4" aria-hidden />
                              Password
                            </Button>
                          )}
                          {a.status === 'allocated' && (
                            <Button variant="ghost" size="sm" onClick={() => returnDevice(a._id)}>
                              <Undo2 className="h-4 w-4" aria-hidden />
                              Return
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Allocation form */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Allocate Device"
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={submit} loading={mutations.createAllocation.isPending}>Allocate</Button>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Select
              label="Device (Asset ID)"
              name="assetId"
              placeholder={devices.length ? 'Select a device' : 'No available devices in Fixed Assets'}
              options={devices.map((d) => ({ label: `${d.assetCode} — ${d.name}`, value: d._id }))}
              value={form.assetId}
              onChange={(e) => selectDevice(e.target.value)}
            />
          </div>
          <Input label="Category" name="category" value={form.category ? humanize(form.category) : ''} readOnly placeholder="From device" />
          <Input label="Brand" name="brand" value={form.brand} onChange={(e) => setForm((p) => ({ ...p, brand: e.target.value }))} />
          <Input label="Model Number" name="modelNumber" value={form.modelNumber} onChange={(e) => setForm((p) => ({ ...p, modelNumber: e.target.value }))} />
          <Input label="Serial Number (S/N)" name="serialNumber" value={form.serialNumber} onChange={(e) => setForm((p) => ({ ...p, serialNumber: e.target.value }))} />
          <Input label="Warranty" name="warranty" value={form.warranty} onChange={(e) => setForm((p) => ({ ...p, warranty: e.target.value }))} placeholder="e.g. 1 year / till 2027-05" />
          <Input
            label="Date of Assigning"
            name="dateOfAssigning"
            type="date"
            value={form.dateOfAssigning}
            onChange={(e) => setForm((p) => ({ ...p, dateOfAssigning: e.target.value }))}
          />
          <Select
            label="Employee"
            name="employeeUserId"
            placeholder="Select an employee"
            options={users.map((u) => ({ label: u.display_name, value: u.id }))}
            value={form.employeeUserId}
            onChange={(e) => selectEmployee(e.target.value)}
          />
          <Input label="Employee Department" name="employeeDepartment" value={form.employeeDepartment ? humanize(form.employeeDepartment) : ''} readOnly placeholder="From employee" />
          <Input
            label="Device Password"
            name="devicePassword"
            type="password"
            autoComplete="new-password"
            value={form.devicePassword}
            onChange={(e) => setForm((p) => ({ ...p, devicePassword: e.target.value }))}
            hint="Stored encrypted; visible to HR Admins only."
          />
        </div>
      </Modal>

      {/* Revealed password */}
      <Modal
        open={!!revealed}
        onClose={() => setRevealed(null)}
        title="Device Password"
        size="sm"
        footer={<Button variant="outline" onClick={() => setRevealed(null)}>Close</Button>}
      >
        {revealed && (
          <div className="space-y-3">
            <p className="text-body-sm text-on-surface-variant">
              Device password for <span className="font-mono text-on-surface">{revealed.assetCode}</span>
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all rounded-DEFAULT border border-outline bg-surface-variant px-3 py-2 text-data-md text-on-surface">
                {revealed.password}
              </code>
              <Button variant="outline" size="icon" onClick={copyPassword} aria-label="Copy password">
                <Copy className="h-4 w-4" aria-hidden />
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
